<template>
  <div :class="['model-slider-wrapper', { popup: isPopUpImages }]">
    <div :class="['model-slider', { popup: isPopUpImages }]">
      <UiPIcon name="icon-close" class="icon-close grey" @click="onClose" />
      <div ref="baseSquareSize" class="model-slider__base">
        <swiper
          class="base-swiper"
          :navigation="{
            prevEl: isReviews ? '.base-prev-reviews' : '.base-prev',
            nextEl: isReviews ? '.base-next-reviews' : '.base-next',
            enabled: true,
          }"
          :pagination="{
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 7,
            enabled: isTablet,
          }"
          :modules="[Navigation, Thumbs, Pagination]"
          :grab-cursor="true"
          :space-between="100"
          :auto-height:="true"
          :initial-slide="indexViewImage"
          :thumbs="{ swiper: thumbsSwiper, multipleActiveThumbs: true }"
          @click="onClick"
        >
          <button
            class="arrow arrow-prev"
            :class="isReviews ? 'base-prev-reviews' : 'base-prev'"
          >
            <span class="slider_triangle slider_triangle--left">
              <span class="slider_triangle__elem" />
            </span>
          </button>
          <button
            class="arrow arrow-next"
            :class="isReviews ? 'base-next-reviews' : 'base-next'"
          >
            <span class="slider_triangle slider_triangle--right">
              <span class="slider_triangle__elem" />
            </span>
          </button>
          <swiper-slide
            v-for="(item, index) in props.images"
            :key="`slide-${index}`"
            class="base-slide"
          >
            <UiResponsiveImage
              v-if="isPopUpImages"
              class="base-image"
              :src="item || '/static/images/no-img.svg'"
              :alt="name + (index ? ' – фото ' + index : '')"
              :title="name + (index ? ' – фото ' + index : '')"
              :width="100"
              :height="100"
              :loading="index === 0 ? undefined : 'lazy'"
              draggable="false"
              :adaptive-image="true"
              :mobile-sizes="{ width: 2000, height: 2000 }"
              :desktop-sizes="{ width: 2000, height: 2000 }"
              :load-original="false"
            />
            <UiResponsiveImage
              v-else
              class="base-image"
              :src="item || '/static/images/no-img.svg'"
              :alt="name + (index ? ' – фото ' + index : '')"
              :title="name + (index ? ' – фото ' + index : '')"
              :width="100"
              :height="100"
              :loading="index === 0 ? undefined : 'lazy'"
              draggable="false"
              :adaptive-image="true"
              :mobile-sizes="{ width: 401, height: 401 }"
              :desktop-sizes="{ width: 401, height: 401 }"
              :load-original="false"
            />
          </swiper-slide>
        </swiper>
      </div>
      <div
        v-if="props.images?.length > 1"
        ref="subSquareSize"
        class="model-slider__sub"
      >
        <div
          :class="[
            'sub-slider-arrow',
            `${isReviews ? 'sub-prev' : 'sub-prev-rew'}-${
              props.quickView ? 'q' : 'm'
            }`,
            { 'no-arrow': !isTablet && props.images?.length <= previewNumber },
          ]"
        ></div>
        <swiper
          :ref="isReviews ? 'subSwiper' : 'subSwiperReviews'"
          :space-between="!isTablet ? 12 : 8"
          :slides-per-view="quickView ? 'auto' : previewNumber"
          :allow-touch-move="!props.quickView"
          :watch-slides-progress="true"
          :grab-cursor="true"
          :direction="swiperDirection"
          :modules="[Navigation, Thumbs, FreeMode]"
          :initial-slide="indexViewImage"
          slide-active-class="sub-active"
          :class="isReviews ? 'sub-swiper-reviews' : 'sub-swiper'"
          :navigation="{
            prevEl: `.${isReviews ? 'sub-prev' : 'sub-prev-rew'}-${
              props.quickView ? 'q' : 'm'
            }`,
            nextEl: `.${isReviews ? 'sub-next' : 'sub-next-rew'}-${
              props.quickView ? 'q' : 'm'
            }`,
            enabled: true,
          }"
          @swiper="setThumbsSwiper"
        >
          <swiper-slide
            v-for="(item, index) in props.images"
            :key="`sub-slide-${index}`"
            class="sub-slide"
          >
            <div class="picture-container">
              <UiResponsiveImage
                :src="item || '/static/images/no-img.svg'"
                draggable="false"
                class="sub-image"
                alt=""
                :title="name + (index ? ' – фото ' + index : '')"
                :mobile-sizes="{ width: 40, height: 40 }"
                :desktop-sizes="SubDesktopSizes"
              />
            </div>
          </swiper-slide>
        </swiper>
        <div
          :class="[
            'sub-slider-arrow',
            `${isReviews ? 'sub-next' : 'sub-next-rew'}-${
              props.quickView ? 'q' : 'm'
            }`,
            { 'no-arrow': !isTablet && props.images?.length <= previewNumber },
          ]"
        ></div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { Navigation, Thumbs, FreeMode, Pagination } from 'swiper/modules';
import type SwiperClass from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/scss/free-mode';
import { useMagicKeys } from '@vueuse/core';
import { useMediaQueryStore } from '~/stores/mediaQuery';

const props = defineProps({
  images: {
    type: Object as any,
    required: true,
  },
  mobileSizes: {
    type: Object as any,
    default: () => ({ width: 296, height: 296 }),
  },
  desktopSizes: {
    type: Object as any,
    default: () => ({ width: 520, height: 520 }),
  },
  name: {
    type: String,
    default: 'Slider',
  },
  quickView: {
    type: Boolean,
    default: false,
  },
  indexViewImage: {
    type: Number,
    default: 0,
  },
  isReviews: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['close', 'set-zoom']);
const baseSquareSize = ref<HTMLElement>();
const subSquareSize = ref<HTMLElement>();
const mediaStore = useMediaQueryStore();
const isTablet = computed(
  () =>
    mediaStore.isLessEs || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);
const swiperDirection = computed(() => {
  if (mediaStore.isLg && isPopUpImages.value) {
    return 'horizontal';
  } else if (mediaStore.isLg) {
    return 'vertical';
  } else {
    return isTablet.value ? 'horizontal' : 'vertical';
  }
});
const thumbsSwiper = ref<SwiperClass>();
const isPopUpImages = ref<Boolean>(props.isReviews);
const previewNumber = ref<number | string>(5);
const SubDesktopSizes = computed(() =>
  !isTablet.value ? { width: 52, height: 52 } : { width: 40, height: 40 }
);
const setThumbsSwiper = (swiper: SwiperClass) => {
  thumbsSwiper.value = swiper;
};

const { escape, left, right } = useMagicKeys();
watch(escape, (v) => {
  if (isPopUpImages.value && v) onClose();
});
watch(left, (v) => {
  if (isPopUpImages.value && v) {
    const left = document.querySelector(
      props.isReviews ? '.base-prev-reviews' : '.base-prev'
    );
    if (left) {
      // @ts-ignore
      left.click();
    }
  }
});
watch(right, (v) => {
  if (isPopUpImages.value && v) {
    const right = document.querySelector(
      props.isReviews ? '.base-next-reviews' : '.base-next'
    );
    if (right) {
      // @ts-ignore
      right.click();
    }
  }
});
const onClick = () => {
  if (isPopUpImages.value || props.quickView) return;
  isPopUpImages.value = true;
  emits('set-zoom', true);
  nextTick(() => {
    setSwiper();
  });
};
const onClose = () => {
  if (props.isReviews) {
    emits('close');
  }
  isPopUpImages.value = false;
  emits('set-zoom', false);
  nextTick(() => {
    setSwiper();
  });
};
const setSwiper = () => {
  const html = document.querySelector('html');
  const screen = document.documentElement.clientWidth;
  const swiperDimensionSelector = document.querySelector(
    props.isReviews ? '.sub-swiper-reviews' : '.sub-swiper'
  );
  const subButtons = 72;
  const subPreviews = 76;
  const lastMargin = 12;
  let swiperDimension: number | undefined = 0;
  if (swiperDimensionSelector) {
    swiperDimension = swiperDimensionSelector.clientWidth;
  }

  if (
    baseSquareSize.value &&
    baseSquareSize.value.style &&
    subSquareSize.value &&
    subSquareSize.value.style
  ) {
    if (isPopUpImages.value) {
      html.classList.add('has-modal');
      baseSquareSize.value.style.maxHeight = 'unset';
      baseSquareSize.value.style.maxWidth = 'unset';
      subSquareSize.value.style.maxHeight = 'unset';
      if (screen > 1599) {
        previewNumber.value = 10;
      } else if (screen > 1279) {
        previewNumber.value = 8;
      } else {
        previewNumber.value = Math.round(swiperDimension / 56 - 1);
      }
    } else {
      html.classList.remove('has-modal');
      // previewNumber.value = props.quickView ? 'auto' : screen < 1280 ? 6 : screen > 1599 ? 6 : 4;
      if (screen < 1024) {
        baseSquareSize.value.style.maxHeight = 'unset';
        baseSquareSize.value.style.maxWidth = 'unset';
      } else {
        const mH = baseSquareSize.value.clientWidth;
        baseSquareSize.value.style.maxHeight = mH + 'px';
        if (props.quickView) {
          previewNumber.value = 'auto';
        } else {
          previewNumber.value = Math.round(
            (mH - subButtons + lastMargin) / subPreviews
          );
          subSquareSize.value.style.maxHeight =
            previewNumber.value * subPreviews + subButtons + 'px';
        }
      }
    }
  }
  thumbsSwiper.value?.update();
};
onMounted(() => {
  window.addEventListener('resize', setSwiper);
  // thumbsSwiper.value?.on('resize', setSwiper);
  nextTick(() => {
    setSwiper();
  });
});
</script>

<style scoped lang="scss" src="./PageModelSliderScss.scss"></style>
